import { cn } from "@/lib/utils"
import { ExclamationTriangleIcon, XCircleIcon, CheckCircleIcon } from '@heroicons/react/20/solid'
import { ReactNode } from 'react'

type AlertVariant = 'warning' | 'error' | 'success'

interface AlertBannerProps {
  variant?: AlertVariant
  title: string
  children: ReactNode
  action?: ReactNode
  className?: string
}

const variantStyles = {
  warning: {
    container: "rounded-md bg-yellow-50 p-4 dark:bg-yellow-500/10 dark:outline dark:outline-1 dark:outline-yellow-500/15",
    icon: "size-5 text-yellow-400 dark:text-yellow-300",
    title: "text-sm font-medium text-yellow-800 dark:text-yellow-100",
    description: "mt-2 text-sm text-yellow-700 dark:text-yellow-100/80"
  },
  error: {
    container: "rounded-md bg-red-50 p-4 dark:bg-red-500/15 dark:outline dark:outline-1 dark:outline-red-500/25",
    icon: "size-5 text-red-400",
    title: "text-sm font-medium text-red-800 dark:text-red-200",
    description: "mt-2 text-sm text-red-700 dark:text-red-200/80"
  },
  success: {
    container: "rounded-md bg-green-50 p-4 dark:bg-green-500/10 dark:outline dark:outline-1 dark:outline-green-500/20",
    icon: "size-5 text-green-400",
    title: "text-sm font-medium text-green-800 dark:text-green-200",
    description: "mt-2 text-sm text-green-700 dark:text-green-200/85"
  }
}

const icons = {
  warning: ExclamationTriangleIcon,
  error: XCircleIcon,
  success: CheckCircleIcon
}

export function AlertBanner({
  variant = 'warning',
  title,
  children,
  action,
  className
}: AlertBannerProps) {
  const styles = variantStyles[variant]
  const Icon = icons[variant]

  return (
    <div className={cn(styles.container, className)}>
      <div className="flex">
        <div className="shrink-0">
          <Icon aria-hidden="true" className={styles.icon} />
        </div>
        <div className="ml-3 flex-1">
          <h3 className={styles.title}>{title}</h3>
          <div className={styles.description}>
            {children}
          </div>
          {action && (
            <div className="mt-4">
              {action}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

